// A skill's details (docs/03 "Skill"): name, slug (its directory under `.claude/skills/`) and
// description. The create drawer and the editor share it.
import { type FormEvent, useState } from "react";
import { Button, Input, Textarea } from "rizzui";

import { slugify, slugProblem } from "../catalog/slug";
import { useT } from "../i18n";
import { Hint } from "../ui/SideDrawer";
import { ErrorState } from "../ui/states";
import type { SkillMeta } from "./api";

interface Props {
  initial?: SkillMeta;
  submitLabel: string;
  onSubmit: (meta: SkillMeta) => Promise<void>;
  onCancel?: () => void;
}

export function SkillForm({ initial, submitLabel, onSubmit, onCancel }: Props) {
  const { skills, common } = useT();
  const t = skills.form;
  const [form, setForm] = useState<SkillMeta>(() => initial ?? { name: "", slug: "", description: "" });
  const [slugTouched, setSlugTouched] = useState(initial !== undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const set = (patch: Partial<SkillMeta>) => setForm((f) => ({ ...f, ...patch }));
  const badSlug = form.slug ? slugProblem(form.slug) : undefined;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="space-y-5" onSubmit={(e) => void submit(e)} aria-label={t.label}>
      <Input label={t.name} value={form.name} required onChange={(e) => set({ name: e.target.value, ...(slugTouched ? {} : { slug: slugify(e.target.value) }) })} />
      <div>
        <Input
          label={t.slug}
          value={form.slug}
          required
          error={badSlug}
          inputClassName="font-mono"
          onChange={(e) => {
            setSlugTouched(true);
            set({ slug: e.target.value });
          }}
        />
        {badSlug ? null : <Hint>{t.slugHint(`.claude/skills/${form.slug || "<slug>"}/`)}</Hint>}
      </div>
      <Textarea
        label={t.description}
        value={form.description}
        rows={3}
        placeholder={t.descriptionPlaceholder}
        onChange={(e) => set({ description: e.target.value })}
      />
      {error ? <ErrorState title={t.notSaved} message={error} /> : null}
      <div className="flex justify-end gap-3 border-t border-muted pt-4">
        {onCancel ? (
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            {common.actions.cancel}
          </Button>
        ) : null}
        <Button type="submit" isLoading={busy} disabled={!!badSlug}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
